import { Grid, IconButton, Tooltip, Typography } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { motion } from "framer-motion";
import React from "react";
import { FormattedMessage } from "react-intl";
import styled from "styled-components";
import cuideoTheme from "../../../containers/themes/defaultTheme";
import PrefilterDiscardButton from "../features/contract/components/PrefilterDiscardButton";
import { TPrefilterDiscardCandidate } from "../features/contract/components/PrefilterDiscardButton/PrefilterDiscardButton";
import CreateCommunicationIntentsButton from "./CreateCommIntentsButton";

const StyledFooter = styled.div`
  padding: 1rem 1rem 1rem 5rem;
  background-color: ${cuideoTheme.palette.primary.main};
`;

const StyledRow = styled(Grid)``;

const StyledLeftSection = styled(Grid)`
  text-align: left;
`;

const StyledNumberText = styled(Typography)`
  color: ${cuideoTheme.palette.common.white};
  font-weight: 700;
`;

const StyledCenterSection = styled(Grid)`
  text-align: center;
`;

const StyledRightSection = styled(Grid)`
  text-align: right;
`;

const StyledCloseButton = styled(IconButton)`
  color: ${cuideoTheme.palette.common.white};
`;

const StyledCommunicationIntentWrapper = styled.div`
  .MuiIconButton-root {
    border: none;

    .CMuiIcon {
      color: ${cuideoTheme.palette.common.white};
    }

    &.Mui-disabled .CMuiIcon {
      color: rgba(255, 255, 255, 0.4);
    }
  }
`;

const StyledDiscardWrapper = styled.div`
  .MuiIconButton-root {
    border: none;
    color: ${cuideoTheme.palette.common.white};

    &.Mui-disabled {
      color: rgba(255, 255, 255, 0.4);
    }
  }
`;

const footerVariants = {
  enter: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.25 },
    display: "block",
  },
  exit: {
    opacity: 0,
    y: 25,
    transition: { duration: 0.25 },
    transitionEnd: {
      display: "none",
    },
  },
};

export interface PrefilterMultiSelectFooterProps {
  contractId: string;
  employeeIds: string[];
  isAutoBotEnabled: boolean;
  discardCandidates: TPrefilterDiscardCandidate[];
  canDiscard: boolean;
  onUnselectAll: () => void;
  onCommunicationIntentsCreated: () => void;
  onDiscard: () => void;
}

/**
 * Barra inferior de la pestaña de prefiltro. La monta la página del contrato
 * cuando hay al menos un candidato seleccionado. Como en la pestaña Añadir, los
 * candidatos seleccionados se pueden descartar de la oferta en bloque (si
 * `canDiscard`) y se puede lanzar el bot con el contrato actual. Los candidatos
 * prefiltrados no son valoraciones del contrato, así que la asignación a otros
 * contratos sigue sin estar disponible aquí.
 */
const PrefilterMultiSelectFooter = (props: PrefilterMultiSelectFooterProps) => {
  const {
    contractId,
    employeeIds,
    isAutoBotEnabled,
    discardCandidates,
    canDiscard,
    onUnselectAll,
    onCommunicationIntentsCreated,
    onDiscard,
  } = props;

  return (
    <motion.div
      variants={footerVariants}
      animate="enter"
      initial="exit"
      style={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 2,
      }}
    >
      <StyledFooter>
        <StyledRow
          container
          spacing={8}
          justifyContent="space-between"
          alignItems="center"
        >
          <StyledLeftSection item xs={12} md={4}>
            <StyledNumberText>
              <FormattedMessage
                id="PrefilterMultiSelectFooter.Employees"
                defaultMessage="{numCandidatos} candidatos seleccionados"
                values={{ numCandidatos: employeeIds.length }}
              />
            </StyledNumberText>
          </StyledLeftSection>
          <StyledCenterSection item xs={12} md={4}>
            <Grid container alignItems="center" justifyContent="center">
              {canDiscard && (
                <Grid item>
                  <Tooltip
                    title={
                      <FormattedMessage
                        id="multiselect.Eliminar candidatos del contrato actual"
                        defaultMessage="Eliminar candidatos del contrato actual"
                      />
                    }
                  >
                    <StyledDiscardWrapper>
                      <PrefilterDiscardButton
                        contractId={contractId}
                        candidates={discardCandidates}
                        onDiscard={onDiscard}
                      />
                    </StyledDiscardWrapper>
                  </Tooltip>
                </Grid>
              )}
              <Grid item>
                <StyledCommunicationIntentWrapper>
                  <CreateCommunicationIntentsButton
                    contractId={contractId}
                    employeeIds={employeeIds}
                    isDisabled={isAutoBotEnabled}
                    disabledTooltip={
                      <FormattedMessage
                        id="PrefilterMultiSelectFooter.AutoBotAlreadySent"
                        defaultMessage="El bot ya se ha enviado automáticamente"
                      />
                    }
                    onCommunicationIntentsCreated={
                      onCommunicationIntentsCreated
                    }
                  />
                </StyledCommunicationIntentWrapper>
              </Grid>
            </Grid>
          </StyledCenterSection>
          <StyledRightSection item xs={12} md={4}>
            <StyledCloseButton onClick={onUnselectAll}>
              <CloseIcon />
            </StyledCloseButton>
          </StyledRightSection>
        </StyledRow>
      </StyledFooter>
    </motion.div>
  );
};

export default PrefilterMultiSelectFooter;
